/**
 * A small local directory: email → account id.
 *
 * The vault is indexed by account id, but at sign-in time all we know is the email: without
 * this directory the login page could not tell whether this browser already holds the key,
 * and would ask for the recovery phrase every single time.
 *
 * Holds nothing secret — an email and a UUID the user already knows. The cryptographic
 * material stays in IndexedDB, encrypted.
 */

const STORAGE_KEY = 'nuxaris_vault_accounts';

type Index = Record<string, string>;

function read(): Index {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Index) : {};
  } catch {
    return {};
  }
}

function write(index: Index): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(index));
  } catch {
    /* quota exceeded or private browsing: the user falls back to entering the key */
  }
}

const normalize = (email: string) => email.trim().toLowerCase();

export function rememberVault(email: string, accountId: string): void {
  const index = read();
  index[normalize(email)] = accountId;
  write(index);
}

export function lookupVault(email: string): string | null {
  return read()[normalize(email)] || null;
}

export function forgetVault(email: string): void {
  const index = read();
  delete index[normalize(email)];
  write(index);
}
